///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { PanelActionTooltip } from "@/components/controls/anchored-panel/PanelHeaderActions"
import { formatTimeAgo, InfoField, MoreSection } from "@/components/controls/anchored-panel/panelAtoms"
import {
	parseEnsembleTracks,
	parseTrack,
	parseWatchWarnings,
	stormCategoryLabel,
	stormSource,
	type StormProperties,
	type StormTrackPoint
} from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import { basinName } from "@/earthbrowser/dataset-distribution/hurricanes/StormFeature"
import { cWatchWarningStyle, pointCategoryLabel, stormCategoryHex } from "@/earthbrowser/dataset-distribution/hurricanes/stormDisplay"
import { closestApproach, type ClosestApproach } from "@/earthbrowser/dataset-distribution/hurricanes/stormInsights"
import { convertSpeed, formatDistance, formatSpeed, getSpeedUnit } from "@/earthbrowser/utils/unitFormatters"
import { setStormCutout } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { Cuboid } from "lucide-react"
import { useEffect, useState } from "react"

const cKnotsToMps = 0.514444

// Forecast table shows the next few advisory points, not the full 5 days
const cForecastTableRows = 5

export function AnchoredHurricaneInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "hurricane" ? data : null}
			fallbackTitle={content => content.storm.name ?? "Tropical Cyclone"}
			accentBorder="border-sky-400/20"
			pill={content => (
				<div
					className="h-[3px] w-11 rounded-full"
					style={{ backgroundColor: stormCategoryHex(content.storm.category, content.storm.max_wind_kt) }}
				/>
			)}
			// The card owns pointer events so wheel/touch scrolling works over
			// it instead of zooming the globe; height is clamped to the
			// viewport and the body scrolls internally
			scrollable
			lockMinHeight={false}
			bodyClassName="px-4 pt-2 pb-3 space-y-2"
			headerExtras={content => <ColumnHeaderToggle storm={content.storm} />}
		>
			{(content, ctx) => <HurricaneBody storm={content.storm} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

/**
 * The one action that opens the visually heavy view, so it is deliberately
 * not one more grey icon: a filled pill in the storm's category color, set
 * apart from the shared action cluster by a divider. Opening the column
 * moves focus to the block and this card gives way to the column HUD
 */
function ColumnHeaderToggle({ storm }: { storm: StormProperties }) {
	const dispatch = useAppDispatch()
	const open = useAppSelector(state => state.dialog.stormCutout?.stormId === storm.id)
	const accent = stormCategoryHex(storm.category, storm.max_wind_kt)
	return (
		<div className="flex items-center shrink-0 mr-1 pr-2 border-r border-white/10">
			<PanelActionTooltip label={open ? "Hide the storm column" : "Lift a block of atmosphere off the globe: cloud-top relief from satellite IR, the wind field, forecast intensity"}>
				<button
					onClick={() => dispatch(setStormCutout(open ? null : storm.id))}
					className={cn(
						"pointer-events-auto h-6 pl-2 pr-2.5 flex items-center gap-1.5 rounded-full shrink-0 whitespace-nowrap",
						"text-[11px] font-semibold tracking-wide text-slate-900",
						"shadow-md shadow-black/40 hover:scale-105 hover:shadow-lg transition-all",
						open && "ring-2 ring-white/40"
					)}
					style={{ backgroundColor: accent }}
					aria-pressed={open}
					aria-label={open ? "Hide storm column" : "Show storm column"}
				>
					<Cuboid className="w-3.5 h-3.5" />
					<span>Storm column</span>
				</button>
			</PanelActionTooltip>
		</div>
	)
}

function HurricaneBody({ storm, ctx }: { storm: StormProperties; ctx: PanelShellContext }) {
	const accent = stormCategoryHex(storm.category, storm.max_wind_kt)
	const advisoryDate = new Date(storm.advisory_date)

	const observed = parseTrack(storm.observed_track)
	const forecast = parseTrack(storm.forecast_track)
	const watchWarnings = parseWatchWarnings(storm.watches_warnings)
	const ensemble = parseEnsembleTracks(storm.ensemble_tracks)

	const peak = forecast.reduce<{ wind: number, t: number } | null>((best, p) => {
		if (p.wind_kt !== null && (!best || p.wind_kt > best.wind)) {
			return { wind: p.wind_kt, t: p.t }
		}
		return best
	}, null)

	const [approach, setApproach] = useState<ClosestApproach | null>(null)
	useEffect(() => {
		let cancelled = false
		setApproach(null)
		closestApproach(storm)
			.then(result => { if (!cancelled) setApproach(result) })
			.catch(() => { /* locations table may not be synced yet */ })
		return () => { cancelled = true }
	}, [storm])

	const forecastRows = forecast.filter(p => (p.tau ?? 0) > 0).slice(0, cForecastTableRows)

	return (
		<>
			<div className="space-y-0.5">
				<div className="text-lg font-bold text-white leading-tight">
					{ctx.title}
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<span
						className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border"
						style={{
							color: accent,
							borderColor: `${accent}66`,
							backgroundColor: `${accent}22`
						}}
					>
						{stormCategoryLabel(storm)}
					</span>
					<span className="text-xs text-slate-300">{basinName(storm.basin)}</span>
				</div>
			</div>

			{storm.headline && (
				<div className="text-[10px] leading-snug text-slate-300 border-l-2 pl-2" style={{ borderColor: accent }}>
					{storm.headline}
				</div>
			)}

			{watchWarnings.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{watchWarnings.map(ww => {
						const style = cWatchWarningStyle[ww.code.toUpperCase()]
						if (!style) return null
						return (
							<span
								key={ww.code}
								className="text-[10px] font-semibold px-1.5 py-px rounded-full border"
								style={{
									color: style.hex,
									borderColor: `${style.hex}66`,
									backgroundColor: `${style.hex}1e`
								}}
							>
								{style.label}
							</span>
						)
					})}
				</div>
			)}

			{storm.max_wind_kt !== null && (
				<div className="text-xl font-light text-white leading-none">
					{formatSpeed(storm.max_wind_kt * cKnotsToMps)}
					<span className="ml-2 text-xs text-slate-400 font-normal">sustained</span>
				</div>
			)}

			<MoreSection compact={ctx.compact}>
				<WindSparkline observed={observed} forecast={forecast} accent={accent} />

				{forecastRows.length > 0 && (
					<div className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5">
						<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
							Forecast <span className="normal-case tracking-normal">({getSpeedUnit()})</span>
						</div>
						<div className="grid grid-flow-col auto-cols-fr gap-1 text-center">
							{forecastRows.map(point => {
								const [weekday, hour] = formatPointTimeParts(point.t)
								return (
									<div key={point.t} className="min-w-0">
										<div className="text-[10px] text-slate-400 leading-tight">{weekday}</div>
										<div className="text-[10px] text-slate-500 leading-tight whitespace-nowrap">{hour}</div>
										<div
											className="text-[11px] font-semibold leading-snug whitespace-nowrap"
											style={{ color: stormCategoryHex(point.category, point.wind_kt) }}
										>
											{pointCategoryLabel(point)}
										</div>
										<div className="text-[11px] text-slate-200 tabular-nums leading-tight">
											{point.wind_kt !== null ? Math.round(convertSpeed(point.wind_kt * cKnotsToMps)) : "—"}
										</div>
									</div>
								)
							})}
						</div>
					</div>
				)}

				<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 space-y-1.5">
					<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
						{storm.gust_kt !== null && (
							<InfoField label="Gusts" value={formatSpeed(storm.gust_kt * cKnotsToMps)} />
						)}
						{storm.mslp_mb !== null && (
							<InfoField label="Pressure" value={`${Math.round(storm.mslp_mb)} mb`} />
						)}
						{storm.movement_dir !== null && storm.movement_speed_kt !== null && (
							<InfoField
								label="Movement"
								value={`${cardinalDirection(storm.movement_dir)} at ${formatSpeed(storm.movement_speed_kt * cKnotsToMps)}`}
							/>
						)}
						{peak && peak.wind > (storm.max_wind_kt ?? 0) && (
							<InfoField
								label="Forecast peak"
								value={`${formatSpeed(peak.wind * cKnotsToMps)} ${formatTimeUntil(peak.t)}`}
							/>
						)}
					</div>

					{approach && (
						<div className="text-xs pt-1 border-t border-white/5">
							<span className="text-slate-500">Closest approach </span>
							<span className="text-slate-200">
								{formatDistance(approach.distanceKm)} {approach.direction} of {approach.cityName}
								<span className="text-slate-400"> · {formatPointTime(approach.t)}</span>
							</span>
						</div>
					)}

					{ensemble && ensemble.members.length > 0 && (
						<div className="text-xs pt-1 border-t border-white/5">
							<span className="text-slate-500">Ensemble </span>
							<span className="text-slate-200">
								{ensemble.members.length} ECMWF member tracks
								{ensemble.hres ? " + HRES" : ""}
								<span className="text-slate-400"> · {formatRunLabel(ensemble.run)}</span>
							</span>
						</div>
					)}

					<div className="text-[10px] text-slate-500 pt-1 border-t border-white/5">
						{storm.advisory_num ? `Advisory ${storm.advisory_num} \u2022 ` : ""}
						{formatTimeAgo(advisoryDate)} &bull; Source: {stormSource(storm.basin)}
						{ensemble ? " \u2022 Ensemble: ECMWF" : ""}
					</div>
				</div>
			</MoreSection>
		</>
	)
}

/** Model run label like "12Z Oct 16" */
function formatRunLabel(runIso: string): string {
	const d = new Date(runIso)
	if (Number.isNaN(d.getTime())) return runIso
	const hour = String(d.getUTCHours()).padStart(2, "0")
	const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
	return `${hour}Z ${day} run`
}

/**
 * Sustained wind history and forecast as a small inline sparkline: solid for
 * the observed record, dashed for the forecast, with a tick at "now"
 */
function WindSparkline({ observed, forecast, accent }: {
	observed: StormTrackPoint[]
	forecast: StormTrackPoint[]
	accent: string
}) {
	const lastObservedT = observed.length > 0 ? observed[observed.length - 1].t : -Infinity
	const observedPts = observed.filter(p => p.wind_kt !== null)
	const forecastPts = forecast.filter(p => p.wind_kt !== null && p.t > lastObservedT)
	const all = [...observedPts, ...forecastPts]
	if (all.length < 3) return null

	const width = 100
	const height = 30
	const pad = 2

	const t0 = all[0].t
	const t1 = all[all.length - 1].t
	const winds = all.map(p => p.wind_kt ?? 0)
	const maxWind = Math.max(...winds)
	const minWind = Math.min(...winds)
	const windSpan = Math.max(maxWind - minWind, 10)

	const x = (t: number) => pad + ((t - t0) / (t1 - t0)) * (width - 2 * pad)
	const y = (wind: number) => height - pad - ((wind - minWind) / windSpan) * (height - 2 * pad)
	const path = (points: StormTrackPoint[]) =>
		points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.wind_kt ?? 0).toFixed(1)}`).join(" ")

	// Bridge the observed line into the forecast so the curve is continuous
	const forecastPath = observedPts.length > 0 && forecastPts.length > 0
		? path([observedPts[observedPts.length - 1], ...forecastPts])
		: path(forecastPts)

	const nowX = Date.now() >= t0 && Date.now() <= t1 ? x(Date.now()) : null

	return (
		<div>
			<div className="flex items-baseline justify-between text-[10px] text-slate-500 mb-0.5">
				<span>Wind history + forecast</span>
				<span className="tabular-nums">peak {Math.round(maxWind)} kt</span>
			</div>
			<svg viewBox={`0 0 ${width} ${height}`} className="w-full h-8 block" preserveAspectRatio="none">
				{nowX !== null && (
					<line x1={nowX} y1={0} x2={nowX} y2={height} stroke="#ffffff30" strokeWidth="0.75" />
				)}
				{observedPts.length >= 2 && (
					<path d={path(observedPts)} fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" />
				)}
				{forecastPts.length >= 1 && (
					<path d={forecastPath} fill="none" stroke={accent} strokeWidth="1.25" strokeDasharray="2.5 2" opacity="0.7" strokeLinejoin="round" />
				)}
			</svg>
		</div>
	)
}

function cardinalDirection(degrees: number): string {
	const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
	return directions[Math.round(((degrees % 360) + 360) % 360 / 22.5) % 16]
}

/** "Tue 2 PM" in the viewer's local time */
function formatPointTime(tMs: number): string {
	const [weekday, hour] = formatPointTimeParts(tMs)
	return `${weekday} ${hour}`
}

/** ["Tue", "2 PM"] in the viewer's local time, for stacked compact layouts */
function formatPointTimeParts(tMs: number): [string, string] {
	const date = new Date(tMs)
	return [
		date.toLocaleDateString(undefined, { weekday: "short" }),
		date.toLocaleTimeString(undefined, { hour: "numeric" }),
	]
}

function formatTimeUntil(tMs: number): string {
	const hours = Math.round((tMs - Date.now()) / 3.6e6)
	if (hours <= 0) return "now"
	if (hours < 24) return `in ${hours}h`
	return `in ${Math.round(hours / 24)}d`
}
